import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const generateColors = (categories) => {
  // Base colors - you can adjust these
  const baseColors = [
    '#818CF8', // Indigo
    '#34D399', // Emerald
    '#F472B6', // Pink
    '#FBBF24', // Amber
    '#60A5FA', // Blue
    '#A78BFA', // Purple
    '#FB923C', // Orange
    '#4ADE80', // Green
    '#F87171', // Red
    '#38BDF8', // Sky
  ];

  // Create a map of category names to consistent colors
  const colorMap = {};
  categories.forEach((category, index) => {
    colorMap[category] = baseColors[index % baseColors.length];
  });

  return colorMap;
};

const SinglePieChart = ({ data, currency, colorMap }) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="h-[300px]">
      <h4 className="text-md font-medium text-center">{currency}</h4>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="45%"
            outerRadius={70}
            fill="#8884d8"
            label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((entry) => (
              <Cell 
                key={`cell-${entry.name}`} 
                fill={colorMap[entry.name]}
              />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value) => [`${Number(value).toFixed(2)}`, 'Amount']}
          />
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{
                paddingBottom: "24px",
                bottom: 0
            }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const PieChartStructure = ({ data, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Group data by currency
  const currencyData = useMemo(() => {
    const grouped = {};
    
    data.forEach(item => {
      if (!item.currency) return;
      
      if (!grouped[item.currency]) {
        grouped[item.currency] = {};
      }
      
      if (!grouped[item.currency][item.name]) {
        grouped[item.currency][item.name] = 0;
      }
      
      grouped[item.currency][item.name] += item.value;
    });

    // Convert to array format for each currency
    return Object.entries(grouped).map(([currency, categories]) => ({
      currency,
      data: Object.entries(categories).map(([name, value]) => ({
        name,
        value
      }))
    }));
  }, [data]);

  // Generate colors for all unique categories across all currencies
  const colorMap = useMemo(() => {
    const allCategories = [...new Set(data.map(item => item.name))];
    return generateColors(allCategories);
  }, [data]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev + 1);
  };

  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white rounded-lg p-4">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4">
      {title && (
        <h3 className="text-lg font-semibold mb-2 text-center">{title}</h3>
      )}
      
      {currencyData.length > 0 && (
        <div className="relative">
          {/* Navigation arrows */}
          {currencyData.length > 1 && (
            <>
              {currentIndex > 0 && (
                <button
                  onClick={handlePrevious}
                  className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white/80 hover:bg-gray-100 rounded-full p-2 shadow-md"
                  aria-label="Previous currency"
                >
                  <FaChevronLeft className="text-gray-600" />
                </button>
              )}
              {currentIndex < currencyData.length - 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white/80 hover:bg-gray-100 rounded-full p-2 shadow-md"
                  aria-label="Next currency"
                >
                  <FaChevronRight className="text-gray-600" />
                </button>
              )}
            </>
          )}

          {/* Currency indicator */}
          {currencyData.length > 1 && (
            <div className="text-center mb-2 text-sm text-gray-500">
              {currentIndex + 1} / {currencyData.length} currencies
            </div>
          )}

          {/* Current pie chart */}
          <SinglePieChart
            data={currencyData[currentIndex].data}
            currency={currencyData[currentIndex].currency}
            colorMap={colorMap}
          />
        </div>
      )}
    </div>
  );
};

export default PieChartStructure; 